import type ArrayProxy from '@ember/array/proxy';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { alias } from '@ember/object/computed';
import type Transition from '@ember/routing/transition';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { addMinutes } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import { dropTask } from 'ember-concurrency';
import config from 'garaje/config/environment';
import type BillingCompanyModel from 'garaje/models/billing-company';
import type CompanyModel from 'garaje/models/company';
import type DeskLocationModel from 'garaje/models/desk-location';
import type FeatureModel from 'garaje/models/feature';
import type LocationModel from 'garaje/models/location';
import type LocationSubscriptionModel from 'garaje/models/location-subscription';
import type PluginInstallModel from 'garaje/models/plugin-install';
import type SubscriptionModel from 'garaje/models/subscription';
import type SubscriptionPlanModel from 'garaje/models/subscription-plan';
import type TenantModel from 'garaje/models/tenant';
import type TenantConnectionRequestModel from 'garaje/models/tenant-connection-request';
import type UserModel from 'garaje/models/user';
import type { CurrentZoneRouteModel } from 'garaje/pods/current-zone/route';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FlowService from 'garaje/services/flow';
import type InactivityService from 'garaje/services/inactivity';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import { resolveRouteScope } from 'garaje/services/route-scope';
import withCompanyStateFunctionality from 'garaje/services/state/company-state';
import withLocationStateFunctionality from 'garaje/services/state/location-state';
import withSubscriptionStateFunctionality from 'garaje/services/state/subscription-state';
import type { CompanyMeta } from 'garaje/services/state/user-state';
import withUserStateFunctionality from 'garaje/services/state/user-state';
import withZoneStateFunctionality from 'garaje/services/state/zone-state';
import type UserDocumentService from 'garaje/services/user-document';
import type { RecordArray } from 'garaje/utils/type-utils';

const Base = withUserStateFunctionality(
  withSubscriptionStateFunctionality(
    withCompanyStateFunctionality(withZoneStateFunctionality(withLocationStateFunctionality(Service))),
  ),
);

type AnySubscriptionModel = LocationSubscriptionModel | SubscriptionModel;
type MaybeSubscriptionModel = AnySubscriptionModel | null;

class StateServiceInternal extends Base {
  @tracked allPlans: ArrayProxy<SubscriptionPlanModel> | null = null;
  @tracked currentCompany: CompanyModel | null = null;
  @tracked billingCompany: BillingCompanyModel | null = null;
  @tracked currentLocation: LocationModel | null = null;
  @tracked currentUser: UserModel | null = null;
  @tracked _companyMeta: CompanyMeta | null = null;
  @tracked _companyId: string | null = null;
  @tracked zoneState: CurrentZoneRouteModel | null = null;
  @tracked companyFeatures: FeatureModel | null = null;
  @tracked locationFeatures: FeatureModel | null = null;
  @tracked companyConnectSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationConnectSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyDeliveriesSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationDeliveriesSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyDesksSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationDesksSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyEmnoSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationEmnoSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyRoomsSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationRoomsSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyVfdSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationVfdSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyVisitorsSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationVisitorsSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyWorkplaceSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationWorkplaceSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyScreensSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationScreensSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyReservationsSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationReservationsSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked companyPlatformSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked locationPlatformSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked realDeliveriesSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked realRoomsSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;
  @tracked realDesksSubscription: LocationSubscriptionModel | SubscriptionModel | null = null;

  @tracked showDeliveriesOnboarding = false;

  @tracked tenantConnectionRequests: RecordArray<TenantConnectionRequestModel> | null = null;
  @tracked tenantConnections: RecordArray<TenantModel> | null = null;

  @tracked activeLocations = [];
  @tracked activeDeliveryAreas = [];
  @tracked activeRooms = [];
  @tracked activeEmployees = [];
  @tracked activeDeskLocations: DeskLocationModel[] = [];
  @tracked allDeskLocations: DeskLocationModel[] = [];

  @tracked pluginInstalls: PluginInstallModel[] = [];

  /** @deprecated Read-only alias for `visitorsSubscription`. Use `visitorsSubscription` directly. */
  get vrSubscription(): this['visitorsSubscription'] {
    return this.visitorsSubscription;
  }
  @alias('billingCompany.resellerPartner') resellerPartner!: BillingCompanyModel['resellerPartner'];

  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service('flow') declare employeeScreeningFlowService: FlowService;
  @service declare inactivity: InactivityService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service('user-document') declare userDocumentService: UserDocumentService;

  #nextRouteName: string | null = null;

  constructor(properties: object) {
    super(properties);
    this.router.on('routeWillChange', (transition: Transition) => this.#routeWillChange(transition));
    this.router.on('routeDidChange', () => {
      this.#nextRouteName = null;
    });
  }

  #routeWillChange(transition: Transition): void {
    const nextRoute = transition.to;
    this.#nextRouteName = nextRoute?.name ?? null;
    void transition.catch(() => {
      if (this.#nextRouteName === nextRoute?.name) {
        this.#nextRouteName = null;
      }
    });
  }

  get #effectiveRouteName(): string | null {
    return this.#nextRouteName ?? this.router.currentRouteName;
  }

  get currentLocationEnabledDesks() {
    return this.allDeskLocations.find((dl) => dl.belongsTo('location').id() === this.currentLocation?.id);
  }

  /**
   * WRKX-5966: Under new pricing, desks access comes via the reservations subscription
   * and should bypass desk-location activation checks.
   *
   * Checks subscription existence only, not status — this matches how all other
   * subscription properties are assigned in subscription-state.ts (no status filtering).
   * Active-status gating is handled by featureConfig.isEnabled('desks') which queries
   * the billing API, which only returns features from subscribed/trial subscriptions.
   *
   * Under locationBilling, reservationsSubscription is a LocationSubscription record
   * loaded from currentLocation.locationSubscriptions — so this check is inherently
   * location-scoped. Without locationBilling, it is a company-level Subscription and
   * is not location-scoped; per-location gating then relies on featureConfig.
   */
  get hasReservationsDesks(): boolean {
    return this.featureFlags.isEnabled('wrkx-pricing-packaging') && !!this.reservationsSubscription;
  }

  /**
   * Whether a desk-location record exists for the current location (regardless of active
   * state), or the customer has desks via the reservations bundle (WRKX-5966).
   *
   * Used by desks/index, desks/get-started, and desks/landing-page routes, which show
   * an InactivatedLocation reactivation banner for inactive desk-locations rather than
   * redirecting away.
   */
  get hasDesksAtLocation(): boolean {
    return !!this.currentLocationEnabledDesks || this.hasReservationsDesks;
  }

  /**
   * Whether desks are active at the current location — the desk-location record is
   * present AND active, or the customer has desks via the reservations bundle (WRKX-5966).
   *
   * Used by resources routes and desks/settings which have no InactivatedLocation
   * component and should redirect to the landing/upsell page when desks are inactive.
   *
   * When locationBilling is enabled, both the desk-location check and hasReservationsDesks
   * are location-scoped (LocationSubscription is loaded per-location). Without
   * locationBilling, hasReservationsDesks is company-scoped, which means reservations-
   * bundle customers cannot disable desks at individual locations via the desk-location
   * active flag. Per-location feature gating is still enforced by
   * featureConfig.isEnabled('desks') (which queries billing with location_id) in the
   * DeskAbility.hasDesksAccess / can('access desk') check that routes evaluate separately.
   *
   * When hasReservationsDesks is true but allDeskLocations is empty (e.g., desk-location
   * was deleted rather than deactivated), routes proceed but downstream model hooks
   * return empty data ({ workplaceDays: [] }) via null-safety guards.
   */
  get isDesksActiveAtLocation(): boolean {
    return !!this.currentLocationEnabledDesks?.active || this.hasReservationsDesks;
  }

  get features(): FeatureModel | null {
    const scope = resolveRouteScope(this.#effectiveRouteName);
    if (scope === 'company' || scope === 'zone') return this.companyFeatures;
    // On a location route:
    //   - Default branch: locationFeatures is null by design; companyFeatures
    //     is the only record, so fall back.
    //   - locationBilling: companyFeatures === locationFeatures, so the
    //     fallback is a no-op.
    //   - MPB: locationFeatures and companyFeatures legitimately diverge. A
    //     null locationFeatures means the location truly has no features —
    //     do NOT fall back to the company-aggregate, which would expose
    //     features the location lacks.
    if (this.featureFlags.isEnabled('company-has-mixed-plan-billing')) return this.locationFeatures;
    return this.locationFeatures ?? this.companyFeatures;
  }

  /**
   * Resolves the appropriate subscription slot based on the current route's
   * scope. On a company- or zone-prefixed route, returns the company-scoped
   * slot; otherwise falls back to `location ?? company`.
   */
  private _scopedSubscription(
    company: LocationSubscriptionModel | SubscriptionModel | null,
    location: LocationSubscriptionModel | SubscriptionModel | null,
  ): LocationSubscriptionModel | SubscriptionModel | null {
    const scope = resolveRouteScope(this.#effectiveRouteName);
    if (scope === 'company' || scope === 'zone') return company;
    return location ?? company;
  }

  get visitorsSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyVisitorsSubscription, this.locationVisitorsSubscription);
  }

  get deliveriesSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyDeliveriesSubscription, this.locationDeliveriesSubscription);
  }

  get roomsSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyRoomsSubscription, this.locationRoomsSubscription);
  }

  get desksSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyDesksSubscription, this.locationDesksSubscription);
  }

  get workplaceSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyWorkplaceSubscription, this.locationWorkplaceSubscription);
  }

  get platformSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyPlatformSubscription, this.locationPlatformSubscription);
  }

  get reservationsSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyReservationsSubscription, this.locationReservationsSubscription);
  }

  get screensSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyScreensSubscription, this.locationScreensSubscription);
  }

  get connectSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyConnectSubscription, this.locationConnectSubscription);
  }

  get emnoSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyEmnoSubscription, this.locationEmnoSubscription);
  }

  get vfdSubscription(): LocationSubscriptionModel | SubscriptionModel | null {
    return this._scopedSubscription(this.companyVfdSubscription, this.locationVfdSubscription);
  }

  get hasAnyFeatures(): boolean {
    return !!this.features?.hasAnyFeatures;
  }

  get deliveriesOnly(): boolean {
    return Boolean(this.deliveriesSubscription && !this.roomsSubscription && !this.visitorsSubscription);
  }

  get roomsOnly(): boolean {
    return Boolean(
      !this.features?.canAccessDeliveriesApplication &&
        !!this.features?.canAccessRoomsApplication &&
        !this.visitorsSubscription,
    );
  }

  get visitorsOnly(): boolean {
    return Boolean(!this.deliveriesSubscription && !this.roomsSubscription && this.visitorsSubscription);
  }

  @action
  minutesBetweenTimezones(date: Date = new Date()): number {
    const locationTimezone = this.currentLocation!.timezone;
    const locationOffset = (getTimezoneOffset(locationTimezone, date) || 0) / -60000; // offset in minutes
    const userOffset = new Date(date).getTimezoneOffset(); //offset in minutes
    return locationOffset - userOffset;
  }

  @action
  getOfficeLocationTime(userLocationTime: Date): Date {
    return addMinutes(userLocationTime, this.minutesBetweenTimezones(userLocationTime));
  }

  // Initializes the app's state optimally by running requests concurrently
  initializeState = dropTask(async (loadedFrom?: string) => {
    /*
      The requests are batched into 3 groups, prioritizing the earliest
      a request can be sent. Each batch is dependent on information
      requested in the batch before it.
    */

    // BATCH #1
    // Request user, company, location(s), zone(s) & subscription
    const batch1 = await Promise.all([
      this.initUserState.perform(),
      this.initCompanyState.perform(),
      this.initZoneState.perform(),
      this.preloadLocations.perform(loadedFrom),
      this.store.query('subscription', { filter: { company_id: this._companyId } }),
      ...(this._companyId ? [this.store.queryRecord('feature', { filter: { company_id: this._companyId } })] : []),
    ]);

    // Configures currentLocation using prefetched data
    await this.initLocationState.perform(loadedFrom);

    // BATCH #2
    // Request split flags & feature configuration
    if (this.currentCompany && this.currentUser && this.currentLocation) {
      // when in a location
      this.locationFeatureFlags.setProperty(this.currentLocation);

      await Promise.all([
        this._setupUserAndCompanyFeatureFlags(this.currentUser, this.currentCompany),
        this.locationFeatureFlags.ready(), // Location feature flags
        this.featureConfig.fetchFeatureConfigTask.perform(this.currentLocation.id), // Feature config
      ]);
    } else if (this.currentCompany && this.currentUser && this.currentZone) {
      // when in a property
      await this._setupUserAndCompanyFeatureFlags(this.currentUser, this.currentCompany);
    }

    // Configures subscription info using prefetched data
    await this.initSubscriptionStateTask.perform(undefined, batch1[4].slice(), batch1[5]);

    // BATCH #3
    // Send remaining requests
    const promises = [
      this.loadTenantConnectionRequests.perform(),
      this.userDocumentService.initialize.perform(),
      this.employeeScreeningFlowService.initService(),
    ];

    if (this.currentCompany) {
      promises.push(
        this.authz.fetchPermissions(),
        this.inactivity.initialize(),
        this.fetchCompanyConfigMeta.perform(),
        this.fetchCompanyBilling.perform(),
        this.fetchCompanyContacts.perform(),
        this.fetchAnalyticsSettings.perform(),
      );
    }

    if (this.currentLocation) {
      promises.push(this.loadTenantConnections.perform());
    }

    if (this.currentLocation && this.currentUser && this.currentCompany) {
      promises.push(
        this.currentAdmin.setEmployeeTask.perform({
          currentUser: this.currentUser,
          currentCompany: this.currentCompany,
          currentLocation: this.currentLocation,
        }),
        this.currentAdmin.setRolesTask.perform({
          currentUser: this.currentUser,
          currentCompany: this.currentCompany,
          currentLocation: this.currentLocation,
        }),
      );
    }

    const { cookieDomain: domain } = config;

    document.cookie = `location_id=${this.currentLocation?.id};domain=${domain};path=/;samesite=lax`;

    await Promise.all(promises);
  });
}

class StateService extends StateServiceInternal {
  declare currentCompany: NonNullable<StateServiceInternal['currentCompany']>;
  declare currentUser: NonNullable<StateServiceInternal['currentUser']>;
  declare currentLocation: NonNullable<StateServiceInternal['currentLocation']>;
  declare billingCompany: NonNullable<StateServiceInternal['billingCompany']>;
}

export default StateService;

export { AnySubscriptionModel, MaybeSubscriptionModel };

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    state: StateService;
  }
}
